import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { money } from "@/lib/api";

const CITY_COORDS = {
  "Kinshasa": [-4.34, 15.27], "Kwango": [-5.03, 17.50], "Lubumbashi": [-11.66, 27.48],
  "Goma": [-1.68, 29.22], "Kisangani": [0.52, 25.20], "Matadi": [-5.82, 13.46],
  "Bukavu": [-2.51, 28.84], "Mbuji-Mayi": [-6.15, 23.60], "Lualaba": [-10.72, 25.47],
};

const DRC_CENTER = [-2.88, 23.66];

const SECTOR_COLOR = {
  "Énergie": "#EAB308", "Agriculture": "#16A34A", "Infrastructure": "#64748B",
  "Mines": "#64748B", "Technologie": "#9333EA", "Numérique": "#0891B2", "Immobilier": "#EA580C", "Santé": "#E11D48",
};

const makeIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="width:26px;height:26px;background:${color};border:3px solid white;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -24],
  });

function coordsFor(p) {
  if (typeof p.lat === "number" && typeof p.lng === "number") return [p.lat, p.lng];
  return CITY_COORDS[p.location] || DRC_CENTER;
}

// Recentre / zoome automatiquement selon les projets affichés (filtre secteur)
function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) { map.setView(DRC_CENTER, 5); return; }
    if (points.length === 1) { map.setView(points[0], 9, { animate: true }); return; }
    map.fitBounds(points, { padding: [60, 60], maxZoom: 9, animate: true });
  }, [points, map]);
  return null;
}

export default function ProjectsMap({ projects }) {
  const points = projects.map(coordsFor);
  const sectors = [...new Set(projects.map((p) => p.sector))];
  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-200" data-testid="projects-map">
      <MapContainer center={DRC_CENTER} zoom={5} scrollWheelZoom={false} style={{ height: "520px", width: "100%" }}>
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <AutoFit points={points} />
        {projects.map((p) => (
          <Marker key={p.id} position={coordsFor(p)} icon={makeIcon(SECTOR_COLOR[p.sector] || "#64748B")}>
            <Popup>
              <div style={{ width: 200 }}>
                <img src={p.image_url} alt="" style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 8 }} onError={(e) => (e.target.style.display = "none")} />
                <p style={{ fontWeight: 700, margin: "6px 0 2px", fontFamily: "Outfit, sans-serif" }}>{p.title}</p>
                <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>{p.sector} · {p.location}</p>
                <p style={{ fontSize: 12, margin: "4px 0" }}>{money(p.amount_collected)} / {money(p.funding_goal)} ({p.progress_pct}%)</p>
                <Link to={`/projets/${p.id}`} style={{ color: "#64748B", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>Voir le projet →</Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {sectors.length > 0 && (
        <div className="absolute bottom-3 left-3 z-[500] bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl px-3 py-2.5 shadow-md" data-testid="map-legend">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Secteurs</p>
          <div className="space-y-1">
            {sectors.map((s) => (
              <div key={s} className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: SECTOR_COLOR[s] || "#64748B" }} />
                <span className="text-xs text-slate-700">{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
